/**
 * What the service actually SAID when it refused (tasks 863, 864).
 *
 * The delete lane's rule is the one task 858 established for deploys: a failed
 * destructive action reports the server's own refusal instead of offering a
 * guess at the cause. `ReleasesView` reads that shape inline and deliberately
 * keeps it local; the delete lane has three surfaces reading it — the AI Service
 * row, the AI Service page, and every `DeleteResourceDialog` — so here it is a
 * named reader rather than the same seven lines copied three times.
 *
 * The shape comes from the axios response interceptor in `src/services/api.ts`,
 * which lifts `data.error` onto `message`, `response.status` onto `status`,
 * `data.details` onto `details`, and `data.details.validationErrors` onto
 * `validationErrors`.
 */

/** The fields the response interceptor attaches to every rejected request. */
export interface ApiRefusal {
  message?: string;
  status?: number;
  details?: unknown;
  validationErrors?: Array<{ field?: string; message?: string }>;
}

export const asApiRefusal = (error: unknown): ApiRefusal => (error ?? {}) as ApiRefusal;

/**
 * One sentence naming why the service refused.
 *
 * Order: the FIELD message first, because it is the one line that names the
 * next action; then the top-level message; then a fallback that says only that
 * there was no reason — never an invented one.
 */
export function refusalReason(error: unknown): string {
  const err = asApiRefusal(error);

  const field = err.validationErrors?.find((entry) => entry.message?.trim())?.message?.trim();
  if (field) return field;

  const top = typeof err.message === 'string' ? err.message.trim() : '';
  if (top) return top;

  return 'The service refused and gave no reason.';
}

/**
 * The scenarios a start was refused for (task 1229, on task 1228's guard): a
 * 409 `IN_FLIGHT` names each scenario already running on the target in
 * `details.inFlight`. Null for any other failure.
 */
export function inFlightRefusal(error: unknown): string[] | null {
  const err = asApiRefusal(error);
  if (err.status !== 409) return null;
  const inFlight = (err.details as { inFlight?: Array<{ name?: string; testId?: string }> } | undefined)?.inFlight;
  if (!Array.isArray(inFlight) || inFlight.length === 0) return null;
  return inFlight.map((entry) => entry.name || entry.testId || 'a scenario');
}

/** The toast for a refused start: "Already running on this target" and the names. */
export const IN_FLIGHT_TOAST_TITLE = 'Already running on this target';
export const inFlightToastDetail = (names: string[]) =>
  `${names.join(', ')} ${names.length === 1 ? 'is' : 'are'} still running in another test. Wait for it to finish, or run on another target.`;
